import * as React from "react";
import { RootState } from "../../redux";
import { ConnectedProps, connect } from "react-redux";
import { IPageSettings } from "../../interfaces/IPageSettings";
import { IFile } from "../../interfaces/IFile";
import { ISharePointFolder } from "../../interfaces/ISharePointFolder";
import {
  initialFileOptionPanelInfoSetting,
  initialFolderOptionPanelInfoSetting,
  initialGraphSearchPager,
  piiConfig,
  validSaveModes,
} from "../../config/constants";
import { IFileFolderSearchSharepointParams } from "../../interfaces/IFileFolderSearchSharepointParams";
import { getItemCustomSelect, searchDriveForFilesAndFoldersSharepoint } from "../../services/GraphService";
import { DriveSearchResultMapper } from "../helpers/DriveSearchResultMapper";
import { UrlSearchBuilderFactory } from "../helpers/sharepointSearch/factory/UrlSearchBuilderFactory";
import {
  ActionButton,
  IIconProps,
  IStackStyles,
  Spinner,
  SpinnerSize,
  Stack
} from "@fluentui/react";
import { LanguageContext } from "../../../../utilities/LocalizationModule";
import { IFilePanelInfo } from "../../interfaces/IFilePanelInfo";
import { IFolderPanelInfo } from "../../interfaces/IFolderPanelInfo";
import { FolderTypeEnum } from "../enums/FolderTypeEnum";
import { ComponentTypes } from "../../config/config";
import { setSearch } from "../../redux/modules/search";
import {
  resetWorkingDirectories,
  addWorkingDirectories,
  addWorkingDirectory,
} from "../../redux/modules/workingDirectory";
import {
  setComposeFiles,
  setComposeVisibility,
  setFileExpander
} from "../../redux/modules/file";
import { expanderConstants } from "../../redux/models/constants/expanders";
import ExpanderWithSubtitle from "../directoryComponents/ExpanderWithSubtitle";
import FileOptionPanel from "../panels/FileOptionPanel";
import FolderOptionPanel from "../panels/FolderOptionPanel";
import { SearchedSharepointFoldersDirectoryBuilder } from "../helpers/searchedSharepointFoldersDirectoryBuilder";
import { IGraphSearchPager } from "../../interfaces/IGraphSearchPager";
import savingLocationStructureModule from "../../../../utilities/SavingLocationStructureModule";
import { isMobile } from "../../styles/mobileStyles";
import { logger } from "../../../../utilities/logger";
import { WebPanel } from "../_v2/_shared/WebPanel/WebPanel";
import MultipleFilesSeleted from "../panels/MultipleFilesSeleted";
import authModule from "../../../../utilities/AuthModule";
import { useLazyGetMetaDataFieldsQuery } from "../_v2/redux/api/graph/api-graph-slice";
import { useLazyGetListColumnsQuery } from "../_v2/redux/api/sharepoint/api-sharepoint-slice";
import { Fileview } from "../_v2/components/Explorer/Fileview/Fileview";
import { PIIResult } from "../_v2/redux/api/local/api-local-interfaces";
import Loader from "../Loader";
import { FileSortOptions } from "./fileSortOptions";
import { ISortOption } from "../../interfaces/ISortOption";
import { FileSortOptionHelper } from "../helpers/fileSortOptionHelper";
import { SharePointFolderItem } from "../_v2/components/Explorer/FolderView/SharePointFolderItem";
import { ModeEnum } from "../enums/modeEnum";
import { FilterTypeEnum } from "../enums/FilterTypeEnum";
import { SearchValue } from "../../redux/models/reduxModels";
import { IDirectoryBuilder } from "../../interfaces/IDirectoryBuilder";
import { SearchedOneDriveDirectoryBuilder } from "../helpers/searchedOneDriveDirectoryBuilder";

const searchIconSrc = require("../../../../assets/empty_search.svg")

const EmptySearchStackStyles: Partial<IStackStyles> = {
  root: { position: "absolute", left: "50%", top: "50%", transform: "translate(-50%,-50%)", userSelect: "none" }
};

const mapDispatchToProps = {
  resetWorkingDirectories,
  addWorkingDirectory,
  addWorkingDirectories,
  setSearch,
  setFileExpander,
  setComposeFiles,
  setComposeVisibility,
};

const mapStateToProps = (state: RootState) => {
  return {
    currentDirectory:
      state.workingDirectories.workingDirectories[state.workingDirectories.workingDirectories.length - 1],
    workingDirectories: state.workingDirectories.workingDirectories,
    folderPermissions: state.currentSharepointFolder,
    searchValue: state.search.searchValue.value,
    mode: state.mode.mode,
    canView: state.currentSharepointFolder.canView,
    sites: state.sharePointInfo.sites,
    isLoading: state.loading.isLoading,
    oneDriveWebUrl: state.user.oneDriveWebUrl,
    teams: state.teams.teams,
    hasFileSelected: state.files.files.some((file) => file.isSelected),
    user: state.user,
    searchValueFull: state.search.searchValue
  };
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

interface FileAndFoldersSearchProps extends PropsFromRedux {
  parentViewHasItems: boolean;
  isSearchingCallback?: (isSearching: boolean) => void;
  searchResultsAvailableCallback?: (resultsAvailable: boolean) => void;
  isLoadingCallback?: (isLoading: boolean) => void;
}

const loadMoreResultsIcon: IIconProps = { iconName: "Table" };

const FileAndFoldersSharepointSearch: React.FunctionComponent<FileAndFoldersSearchProps> = (props) => {
  const languageStrings = React.useContext(LanguageContext);
  const [folders, setFolders] = React.useState<ISharePointFolder[]>([]);
  const [files, setFiles] = React.useState<IFile[]>([]);
  const [checkedFiles, setCheckedFiles] = React.useState<IFile[]>([]);
  const [selectedFiles, setSelectedFiles] = React.useState<IFile[]>([]);
  const [hasSearched, setHasSearched] = React.useState<boolean>(false);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [fileOptionPanelInfo, setFileOptionPanelInfo] = React.useState<IFilePanelInfo>(
    initialFileOptionPanelInfoSetting
  );
  const [folderOptionPanelInfo, setFolderOptionPanelInfo] = React.useState<IFolderPanelInfo>(
    initialFolderOptionPanelInfoSetting
  );

  const currentDirectoryPrivate = props.currentDirectory?.type === undefined
    ? { type: ComponentTypes.GeneralSearch, isTeamsFolder: false }
    : props.currentDirectory;

  const [urlSearchFactory] = React.useState(
    UrlSearchBuilderFactory.createUrlSearchBuilder(
      currentDirectoryPrivate.type,
      [],
      props.oneDriveWebUrl
    )
  );
  const searchFoldersPager = React.useRef<IGraphSearchPager>(initialGraphSearchPager);
  const searchFilesPager = React.useRef<IGraphSearchPager>(initialGraphSearchPager);

  const [isLoadingMoreFolderResults, setIsLoadingMoreFolderResults] = React.useState<boolean>(false);
  const [isLoadingMoreFileResults, setIsLoadingMoreFileResults] = React.useState<boolean>(false);

  const [isMultipleFilesSelectedPanelOpen, setIsMultipleFilesSelectedPanelOpen] = React.useState(false);

  const [fileViews, setFileViews] = React.useState<JSX.Element[]>([]);

  const [getMetaDataFields, { data: metaData }] = useLazyGetMetaDataFieldsQuery();
  const [getListColumns, { data: listColumns }] = useLazyGetListColumnsQuery();
  const [getMetaFields, setGetMetaFields] = React.useState(false);
  const [sortOption, setSortOption] = React.useState<ISortOption>();

  React.useEffect(() => {
    if (props.mode === ModeEnum.mailCompose) {
      props.setComposeFiles({ selectedFiles, isGraphQuery: true });
    }
  }, [props.mode, selectedFiles]);

  React.useEffect(() => {
    if (props.mode === ModeEnum.mailCompose) {
      props.setComposeVisibility(fileViews?.length > 0);
    }
  }, [props.mode, fileViews]);

  React.useEffect(() => {
    const setupAsyncWrapper = async () => {
      if (!props.searchValue && !hasSearched) return;

      setSelectedFiles([]);

      if (!props.searchValue && hasSearched) {
        resetSearch();
        return;
      }

      resetSearchPagers();

      if (props.isSearchingCallback) props.isSearchingCallback(true);
      setIsLoading(true);
      setHasSearched(true);
      resetSearchPagers();

      try {
        const [folderResultCount, fileResultCount] = await Promise.all([getSearchedFolders(), getSearchedFiles()]);
        if (props.searchResultsAvailableCallback) {
          props.searchResultsAvailableCallback(folderResultCount > 0 || fileResultCount > 0);
        }
      } catch (error) {
        logger(error, "fileAndFoldersSharepoint.tsx (useEffect)");
      }

      setIsLoading(false);
      if (props.isSearchingCallback) props.isSearchingCallback(false);

      props.setFileExpander(false);
    };
    setupAsyncWrapper();
  }, [props.searchValueFull]);

  React.useEffect(() => {
    (async () => {
      setCheckedFiles([...files]);
      if (files && props.user.tenantEnablePII === true) {
        files.forEach(async selectedFile => {

          if (selectedFile.pii && (
            (selectedFile.pii?.piiDetected === null && selectedFile.pii?.pii === null) ||
            (selectedFile.pii?.piiDetected === false && selectedFile.pii?.pii === null) ||
            (selectedFile.pii?.piiDetected === false && selectedFile.pii?.pii === undefined)
          )) {
            if (getMetaFields) {
              const metaData = await getMetaDataFields({ driveId: selectedFile.driveId, itemId: selectedFile.id, accessToken: await authModule.getGraphAccessToken() }).unwrap();
              selectedFile.siteUrl = metaData.sharepointIds.siteUrl;
              if (metaData?.fields?.[piiConfig.privacySentitiveColumnName] === undefined) {
                try {
                  const listColumns =
                    await getListColumns({
                      siteUrl: selectedFile.siteUrl,
                      sharePointListId: metaData.sharepointIds.listId,
                      accessToken: currentDirectoryPrivate.type == ComponentTypes.OneDrive || currentDirectoryPrivate.type == ComponentTypes.OneDriveSubFolder
                        ? await authModule.getOneDriveAccessToken()
                        : await authModule.getSharePointAccessToken()
                    }).unwrap() as any;
                  if (listColumns?.value?.length === 2) {

                  } else {
                    delete selectedFile.pii;
                  }
                } catch (error) {
                  selectedFile.pii.piiDetected = false;
                  selectedFile.pii.piiError = languageStrings.ErrorConnectingToServer;
                }
              }
              else {
                selectedFile.pii.piiDetected = metaData?.fields?.[piiConfig.privacySentitiveColumnName];
                selectedFile.pii.pii = metaData?.fields?.[piiConfig.privacySentitiveDataColumnName];
              }
              setCheckedFiles([...files]);
            }
          }
        });
      } else {
        setCheckedFiles([...files]);
      }
    })()
  }, [files]);

  React.useEffect(() => {
    setFileViews(
      checkedFiles.map((item, index) => (
        <div key={index}>
          <Fileview
            item={item}
            isMobile={isMobile()}
            tenantEnablePII={props.user.tenantEnablePII}
            selectFile={selectFile}
            openFileOptions={openFileOptions}
          />
        </div>
      ))
    )
  }, [checkedFiles]);

  React.useEffect(() => {
    if (sortOption) {
      setCheckedFiles(prev => FileSortOptionHelper.sort([...prev], sortOption));
    }
  }, [sortOption]);

  const resetSearch = async () => {
    resetSearchPagers();
    setFiles([]);
    setFolders([]);
  };

  const resetSearchPagers = () => {
    searchFoldersPager.current = initialGraphSearchPager;
    searchFilesPager.current = initialGraphSearchPager;
  };

  const getSearchedFolders = async (isLoadMore?: boolean) => {
    if (isLoadMore) {
      setIsLoadingMoreFolderResults(true);
    }

    const folderSearchParams = buildSearchParams(urlSearchFactory.buildFolderQuery, searchFoldersPager.current.from);
    let result: { moreResultsAvailable: boolean; driveItems: ISharePointFolder[] } =
      await searchDriveForFilesAndFoldersSharepoint(folderSearchParams, new DriveSearchResultMapper());

    if (currentDirectoryPrivate.type === ComponentTypes.SharePoint) {
      result.driveItems = result.driveItems.filter(
        (item) => !props.oneDriveWebUrl || !item.webUrl?.includes(props.oneDriveWebUrl)
      );
    }

    if (isLoadMore) {
      setFolders((prevFolders) => [
        ...prevFolders,
        ...result.driveItems.map((item) => ({ ...item, isSearched: true })),
      ]);
      setIsLoadingMoreFolderResults(false);
    } else {
      setFolders(result.driveItems.map((item) => ({ ...item, isSearched: true })));
    }

    searchFoldersPager.current = {
      moreResultsAvailable: result.moreResultsAvailable,
      from: result.moreResultsAvailable ? searchFoldersPager.current.from + result.driveItems.length : 0,
    }

    let totalResultCount: number = isLoadMore ? folders?.length ?? 0 : 0;
    return totalResultCount + (result?.driveItems?.length ?? 0);
  };

  const getSearchedFiles = async (isLoadMore?: boolean, sortOption?: ISortOption) => {
    if (props?.searchValueFull.filterType == FilterTypeEnum.Folder) return 0;
    if (isLoadMore) {
      setIsLoadingMoreFileResults(true);
    }
    const fileSearchParams = buildSearchParams(urlSearchFactory.buildFileQuery, searchFilesPager.current.from);
    const result = await searchDriveForFilesAndFoldersSharepoint(fileSearchParams, new DriveSearchResultMapper());

    setGetMetaFields(true);

    const fileResults: IFile[] = result.driveItems.map((item: any) => ({ ...item, hasGraphItemId: true }));
    if (isLoadMore) {
      setFiles(prevFiles => sortOption ? FileSortOptionHelper.sort(prevFiles.concat(fileResults), sortOption) : prevFiles.concat(fileResults));
      setIsLoadingMoreFileResults(false);
    }
    else {
      setFiles(sortOption ? FileSortOptionHelper.sort(fileResults, sortOption) : fileResults);
    }

    searchFilesPager.current = {
      moreResultsAvailable: result.moreResultsAvailable,
      from: result.moreResultsAvailable ? searchFilesPager.current.from + result.driveItems.length : 0,
    }

    let totalResultCount: number = isLoadMore ? files?.length ?? 0 : 0;
    return totalResultCount + (result?.driveItems?.length ?? 0);
  };

  const buildSearchParams = (query: (currentDirectory: IPageSettings, searchValue: SearchValue) => string, from: number) => {
    const folderqueryString = query(currentDirectoryPrivate, props.searchValueFull);
    return buildSearchParametersWithWebUrl(folderqueryString, from);
  };

  const buildSearchParametersWithWebUrl = (queryString: string, from: number): IFileFolderSearchSharepointParams => {
    return {
      queryString: queryString,
      from: from,
      extraDriveInfo: {
        siteUrl: currentDirectoryPrivate?.webUrl,
      },
    };
  };

  const addFolderDirectory = async (item: ISharePointFolder) => {
    setIsLoading(true);
    if (props.isLoadingCallback) props.isLoadingCallback(true);
    props.setSearch("");

    try {
      let directoryBuilder: IDirectoryBuilder;

      if (!item.webUrl?.includes(props.oneDriveWebUrl)) {
        directoryBuilder = new SearchedSharepointFoldersDirectoryBuilder(
          props.sites,
          props.teams,
          currentDirectoryPrivate?.type,
          props?.currentDirectory?.isTeamsFolder || props?.currentDirectory?.type === ComponentTypes.Teams
        );
      }
      else if (props.user.oneDriveWebUrl) {
        const itemDetails = await getItemCustomSelect(await authModule.getGraphAccessToken(), item.driveId, item.id, "sharepointIds");
        if (itemDetails) {
          const listId: string = itemDetails.sharepointIds?.listId;
          directoryBuilder = listId && new SearchedOneDriveDirectoryBuilder(props.user.oneDriveWebUrl, listId);
        }
      }

      // const directoryBuilder = new SearchedSharepointFoldersDirectoryBuilder(
      //   props.sites,
      //   props.teams,
      //   currentDirectoryPrivate?.type,
      //   props?.currentDirectory?.isTeamsFolder || props?.currentDirectory?.type === ComponentTypes.Teams
      // );

      if (directoryBuilder) {
        const directories = await directoryBuilder.getFolderDirectories(item);
        props.resetWorkingDirectories(directories);
      }

    } catch (error) {
      logger(error, "fileAndFoldersSharepoint.tsx (addFolderDirectory)");
    }

    if (props.isLoadingCallback) props.isLoadingCallback(false);
    setIsLoading(false);
  };

  const onSaveClicked = React.useCallback(async (item: ISharePointFolder) => {
    setIsLoading(true);
    if (props.isLoadingCallback) props.isLoadingCallback(true);

    try {
      const directoryBuilder = new SearchedSharepointFoldersDirectoryBuilder(
        props.sites,
        props.teams,
        currentDirectoryPrivate?.type,
        props?.currentDirectory?.isTeamsFolder || props?.currentDirectory?.type === ComponentTypes.Teams
      );

      const directories = await directoryBuilder.getFolderDirectories(item);
      savingLocationStructureModule.setSavingLocationStructure(directories);

      props.addWorkingDirectory({
        ...directories[directories.length - 1],
        type: ComponentTypes.Saving,
        displayName: languageStrings.Properties,
        isSaveLocationStructureSet: true,
      });
    } catch (error) {
      logger(error, "fileAndFoldersSharepoint.tsx (onSaveClicked)");
    }

    props.setSearch("");
    if (props.isLoadingCallback) props.isLoadingCallback(false);
    setIsLoading(false);
  }, [props]);

  const openFileOptions = (fileInfo: IFile) => {
    setFileOptionPanelInfo({
      ...fileInfo,
      isOpen: true,
      itemId: fileInfo?.hasGraphItemId ? fileInfo.id : null,
      folderId: !currentDirectoryPrivate?.parentId ? null : currentDirectoryPrivate.id,
      filePath:
        fileInfo?.hasGraphItemId && fileInfo?.path
          ? `[${languageStrings.Files}] ${props.workingDirectories
            .slice(0, -1)
            .map((x) => x.displayName)
            .join("/")}${fileInfo.path}`
          : `[${languageStrings.Files}] ${props.workingDirectories.map((x) => x.displayName).join("/")}/${fileInfo.name
          }`,
    });
  };

  const dismissFileOptionPanel = React.useCallback(() => {
    setFileOptionPanelInfo(initialFileOptionPanelInfoSetting);
  }, []);

  const openFolderOptions = React.useCallback((folderInfo: ISharePointFolder) => {
    setFolderOptionPanelInfo({
      isOpen: true,
      folder: folderInfo,
      folderType: FolderTypeEnum.sharepoint,
      isTeamsFolder: currentDirectoryPrivate.isTeamsFolder,
    });
  }, [currentDirectoryPrivate.isTeamsFolder]);

  const dismissFolderOptionPanel = React.useCallback(() => {
    setFolderOptionPanelInfo(initialFolderOptionPanelInfoSetting);
  }, []);

  const selectFile = (item: IFile) => {
    const newFiles = [...checkedFiles];
    const selectedFile = newFiles.find((c) => c.id === item.id);
    if (selectedFile) {
      selectedFile.selected = selectedFile.selected ? !selectedFile.selected : true;
    }
    setCheckedFiles(newFiles);
    setSelectedFiles([...newFiles.filter((x) => x.selected === true)]);
  };

  const isValidDropZone = React.useMemo((): boolean => {
    return validSaveModes.some(item => item === props.mode);
  }, [props.mode]);

  const foldersView = folders
    ? folders.map((item, index) => (
      <SharePointFolderItem
        key={index}
        item={item}
        isMobile={isMobile()}
        hasFileSelected={props.hasFileSelected}
        isValidDropZone={isValidDropZone}
        addFolderDirectory={addFolderDirectory}
        onSaveClicked={onSaveClicked}
        openFolderOptions={openFolderOptions}
      />
    ))
    : null;

  const folderLoadMoreButton = () => {
    return isLoadingMoreFolderResults ? (
      <div style={{ paddingTop: "10px" }}>
        <Spinner size={SpinnerSize.medium} />
      </div>
    ) : (
      searchFoldersPager.current.moreResultsAvailable && (
        <ActionButton
          iconProps={loadMoreResultsIcon}
          allowDisabledFocus
          onClick={async () => await getSearchedFolders(true)}
        >
          {languageStrings.LoadMore}
        </ActionButton>
      )
    );
  };

  const fileLoadMoreButton = () => {
    return isLoadingMoreFileResults ? (
      <div style={{ paddingTop: "10px" }}>
        <Spinner size={SpinnerSize.medium} />
      </div>
    ) : (
      searchFilesPager.current.moreResultsAvailable && (
        <ActionButton
          iconProps={loadMoreResultsIcon}
          allowDisabledFocus
          onClick={async (e) => { e.preventDefault(); await getSearchedFiles(true, sortOption) }}
        >
          {languageStrings.LoadMore}
        </ActionButton>
      )
    );
  };

  const updateFilePii = (fileOptionPanelInfo: IFilePanelInfo, piiResult: PIIResult) => {
    setFiles(files.map(file => {
      return {
        ...file,
        pii: file.id === fileOptionPanelInfo.id ? piiResult : file.pii
      }
    }));
  }

  const fileUpdated = (fileOptionPanelInfo: IFilePanelInfo, newFilename: string) => {
    if (props?.searchValueFull.filterType == FilterTypeEnum.Folder) {
      setFiles([]);
      return;
    }
    setFiles(files.map(file => {
      return {
        ...file,
        name: file.id === fileOptionPanelInfo.id ? newFilename : file.name
      }
    }));
  }

  const updateMultipleFilesPii = (selectedFiles: IFile[]) => {
    setFiles(files.map(file => {
      const selectedFile = selectedFiles.find(x => x.id === file.id);
      return {
        ...file,
        pii: selectedFile ? selectedFile.pii : file.pii
      }
    }));
  }

  const fileDeleted = (fileOptionPanelInfo: IFilePanelInfo) => {
    setFiles(files.filter(x => x.id !== fileOptionPanelInfo.id));
    setSelectedFiles([]);
  }

  const filesDeleted = () => {
    setFiles(files.filter((el) => !selectedFiles.includes(el)));
    setSelectedFiles([]);
    setIsMultipleFilesSelectedPanelOpen(false);
  }

  const onSortApplied = (sortOption: ISortOption) => {
    setSortOption(sortOption);
  }

  return isLoading ? (
    <Loader label={languageStrings.SearchingContent} />
  ) : (
    <>
      {folders?.length > 0 && (
        <>
          <ExpanderWithSubtitle
            items={foldersView}
            title={languageStrings.Folders}
            expanderNameToToggle={expanderConstants.sharepoint.folders}
            extra={folderLoadMoreButton()}
            hasShowMoreButton={files?.length > 0}
          />
          {folderOptionPanelInfo?.isOpen && <FolderOptionPanel info={folderOptionPanelInfo} dismissPanel={dismissFolderOptionPanel} />}
        </>
      )}
      {files?.length > 0 && (
        <>
          <ExpanderWithSubtitle
            items={fileViews}
            itemsSelected={selectedFiles.length}
            title={languageStrings.Files}
            expanderNameToToggle={expanderConstants.sharepoint.files}
            extra={fileLoadMoreButton()}
            hasShowMoreButton={folders?.length > 0}
            selectedItemsMoreClicked={() => setIsMultipleFilesSelectedPanelOpen(true)}
            sortOptions={<FileSortOptions addRelevanceOption={true} onSortApplied={onSortApplied} />}
          />
          {fileOptionPanelInfo?.isOpen && <FileOptionPanel info={fileOptionPanelInfo} dismissPanel={dismissFileOptionPanel} updateFilePii={updateFilePii} fileUpdated={fileUpdated} fileDeleted={fileDeleted} />}
          {isMultipleFilesSelectedPanelOpen && (
            <WebPanel
              isOpen={isMultipleFilesSelectedPanelOpen}
              closePanel={() => setIsMultipleFilesSelectedPanelOpen(false)}
              headerText={`${selectedFiles.length} ${selectedFiles.length === 1
                ? languageStrings.FileSelected.toLowerCase()
                : languageStrings.FilesSelected.toLowerCase()
                }`}
              styles={{ main: { overflow: 'unset' } }}
            >
              <MultipleFilesSeleted
                files={selectedFiles}
                isGraphQuery={true}
                currentDirectory={currentDirectoryPrivate}
                workingDirectories={props.workingDirectories}
                updateMultipleFilesPii={updateMultipleFilesPii}
                filesDeleted={filesDeleted}
                setIsMultipleFilesSelectedPanelOpen={setIsMultipleFilesSelectedPanelOpen}
              />
            </WebPanel>
          )}
        </>
      )}
      {!props.parentViewHasItems && !(folders?.length > 0) && !(files?.length > 0) && (
        <Stack verticalAlign="center" horizontalAlign="center" styles={EmptySearchStackStyles}>
          <Stack.Item align="center">
            <img src={searchIconSrc} style={{ height: 165 }} />
          </Stack.Item>
          <Stack.Item align="center">
            <span className="xb-empty-location-message centerText">{languageStrings.GenericNoResultsFoundText}</span>
          </Stack.Item>
        </Stack>
      )}
    </>
  );
};

export default connector(FileAndFoldersSharepointSearch);